/**
 * Audio -> MP3 helper
 *
 * The browser MediaRecorder API produces webm/ogg (Chrome/Firefox) or mp4
 * (Safari) audio, never MP3. The VOICE OVER content type, the upload pipeline
 * (extension is validated against "mp3") and the player apps all expect an MP3
 * file, so a microphone recording has to be transcoded before it can be reused
 * by the existing FilePicker upload flow.
 *
 * This module decodes a recorded Blob to raw PCM via the Web Audio API and
 * encodes it to MP3 with lamejs, returning a real `.mp3` File that can be fed
 * straight into FilePicker.uploadFile().
 */
import { Mp3Encoder } from '@breezystack/lamejs';

// MP3 only supports a fixed set of sample rates. AudioContext usually reports
// 44100 or 48000 (both valid); anything exotic is mapped to the nearest legal
// rate so lamejs does not choke.
const MP3_SAMPLE_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];

function nearestMp3SampleRate(sampleRate) {
  if (MP3_SAMPLE_RATES.includes(sampleRate)) {
    return sampleRate;
  }
  return MP3_SAMPLE_RATES.reduce((closest, rate) =>
    Math.abs(rate - sampleRate) < Math.abs(closest - sampleRate) ? rate : closest
  );
}

function floatTo16BitPCM(input) {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

async function decodeToAudioBuffer(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    throw new Error('Web Audio API is not supported in this browser.');
  }
  const audioCtx = new AudioContextClass();
  try {
    // decodeAudioData still uses a callback signature on Safari, so wrap it.
    return await new Promise((resolve, reject) => {
      audioCtx.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
    });
  } finally {
    if (typeof audioCtx.close === 'function') {
      audioCtx.close();
    }
  }
}

/**
 * Transcode a recorded audio Blob into an MP3 Blob.
 *
 * @param {Blob} blob recorded audio (webm/ogg/mp4/wav...)
 * @param {Object} [options]
 * @param {number} [options.kbps=128] MP3 bitrate
 * @returns {Promise<Blob>} an `audio/mpeg` blob
 */
export async function encodeAudioBlobToMp3(blob, options = {}) {
  const kbps = options.kbps || 128;
  const audioBuffer = await decodeToAudioBuffer(blob);

  if (!audioBuffer || audioBuffer.length === 0) {
    throw new Error('Recorded audio is empty.');
  }

  // Voice over is mono by design; downmix anything else to keep the file small.
  const numChannels = 1;
  const sampleRate = nearestMp3SampleRate(audioBuffer.sampleRate);
  const encoder = new Mp3Encoder(numChannels, sampleRate, kbps);

  const channelData = audioBuffer.getChannelData(0);
  const samples = floatTo16BitPCM(channelData);

  const mp3Data = [];
  const blockSize = 1152; // lamejs processes 1152 samples per frame
  for (let i = 0; i < samples.length; i += blockSize) {
    const chunk = samples.subarray(i, i + blockSize);
    const encoded = encoder.encodeBuffer(chunk);
    if (encoded.length > 0) {
      mp3Data.push(new Uint8Array(encoded));
    }
  }
  const flushed = encoder.flush();
  if (flushed.length > 0) {
    mp3Data.push(new Uint8Array(flushed));
  }

  const mp3Blob = new Blob(mp3Data, { type: 'audio/mpeg' });
  if (mp3Blob.size === 0) {
    throw new Error('MP3 encoding produced no data.');
  }
  return mp3Blob;
}

/**
 * Transcode a recorded audio Blob into an MP3 File ready for upload.
 *
 * @param {Blob} blob recorded audio
 * @param {string} [fileName] target filename (".mp3" is enforced)
 * @param {Object} [options] see encodeAudioBlobToMp3
 * @returns {Promise<File>}
 */
export async function encodeAudioBlobToMp3File(blob, fileName = 'recording.mp3', options = {}) {
  const mp3Blob = await encodeAudioBlobToMp3(blob, options);
  const safeName = /\.mp3$/i.test(fileName) ? fileName : `${fileName}.mp3`;
  return new File([mp3Blob], safeName, { type: 'audio/mpeg' });
}
